import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Rocket, Megaphone, CalendarDays, MessageSquareText, Ticket } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DataTable } from "@/components/common/DataTable";
import { api } from "@/lib/api";
import { gbp } from "@/data/marketingOptions";

export const MarketingOverview = ({ refreshKey, codes }) => {
  const [cmd, setCmd] = useState(null);
  const load = useCallback(async () => {
    try { setCmd(await api.list("marketing-command")); } catch { setCmd(null); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const followups = cmd?.followups_due ?? [];
  const activity = cmd?.upcoming_activity ?? [];
  const campaigns = cmd?.active_campaigns ?? [];
  const content = cmd?.upcoming_content ?? [];
  const updates = cmd?.recent_updates ?? [];

  const row = (key, main, sub, right) => (
    <li key={key} className="flex items-start justify-between gap-3 border-b border-[#EEF2FB] pb-3 last:border-0 last:pb-0">
      <div className="min-w-0"><p className="truncate text-sm font-semibold text-[#14245F]">{main}</p>{sub && <p className="text-xs text-[#5A6B8C]">{sub}</p>}</div>
      {right}
    </li>
  );

  return (
    <div className="space-y-6" data-testid="marketing-overview">
      {/* 1 — Primary performance area */}
      <SectionCard testid="mkt-ov-codes-card" accent="#0EA5A4" icon={Ticket} eyebrow="Primary · Results" title="Code Performance" subtitle="Sign-ups, conversions and revenue attributed to discount & referral codes">
        {(!codes || codes.length === 0) ? (
          <EmptyState icon={Ticket} title="No codes yet" description="Add a discount code to an influencer or campaign to see performance here." />
        ) : (
          <DataTable testid="mkt-ov-codes-table" columns={[
            { key: "code", header: "Code", render: (c) => <span className="font-mono font-bold text-[#0EA5A4]">{c.code}</span> },
            { key: "source", header: "Source" },
            { key: "offer", header: "Offer", render: (c) => c.offer || "—" },
            { key: "prelaunch_signups", header: "Sign-ups", render: (c) => <span className="font-bold text-[#0E7490]">{c.prelaunch_signups}</span> },
            { key: "paid_subscriptions", header: "Paid", render: (c) => <span className="font-bold text-[#15803D]">{c.paid_subscriptions}</span> },
            { key: "revenue_attributed", header: "Revenue", render: (c) => <span className="font-bold text-[#14245F]">{gbp(c.revenue_attributed)}</span> },
          ]} rows={codes} />
        )}
      </SectionCard>

      {/* 2 — Follow-up & activity area */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard testid="mkt-ov-followups-card" accent="#FB6F5A" icon={CalendarClock} eyebrow="Action needed" title="Follow-ups Due" subtitle="Influencers & schools due today or earlier">
          {followups.length === 0 ? <EmptyState icon={CalendarClock} title="No follow-ups waiting" description="You're all caught up — new follow-ups appear here." /> : (
            <ul className="space-y-3" data-testid="mkt-ov-followups-list">{followups.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 rounded-xl bg-[#FB6F5A]/[0.06] px-3 py-2">
                <div className="min-w-0 flex items-center gap-2"><span className="h-2 w-2 shrink-0 rounded-full bg-[#FB6F5A]" /><div className="min-w-0"><p className="truncate text-sm font-semibold text-[#14245F]">{f.name}</p><p className="text-xs text-[#C2410C]">Due {f.date}</p></div></div>
                <StatusBadge status="warning" label={f.kind} />
              </li>
            ))}</ul>
          )}
        </SectionCard>

        <SectionCard testid="mkt-ov-updates-card" accent="#06B6D4" icon={MessageSquareText} eyebrow="Live feed" title="Recent Activity" subtitle="Latest updates across the growth engine">
          {updates.length === 0 ? <EmptyState icon={MessageSquareText} title="No activity yet" description="Marketing updates appear here as the team logs them." /> : (
            <ul className="relative space-y-4 before:absolute before:bottom-2 before:left-[7px] before:top-2 before:w-0.5 before:bg-[#EEF2FB]" data-testid="mkt-ov-updates-list">{updates.map((u) => (
              <li key={u.id} className="relative pl-6">
                <span className="absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#06B6D4] ring-2 ring-[#06B6D4]/25" />
                <p className="text-sm font-semibold text-[#14245F]">{u.title}</p>
                <p className="text-xs text-[#5A6B8C]">{u.author}{u.related_name ? ` · ${u.related_name}` : ""}</p>
              </li>
            ))}</ul>
          )}
        </SectionCard>
      </div>

      {/* 3 — Upcoming content area */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard testid="mkt-ov-content-card" accent="#7C3AED" icon={CalendarDays} eyebrow="Calendar" title="Upcoming Content" subtitle="Next scheduled pieces">
          {content.length === 0 ? <EmptyState icon={CalendarDays} title="Nothing scheduled" description="Upcoming content appears here." /> : (
            <ul className="space-y-3" data-testid="mkt-ov-content-list">{content.map((c) => row(c.id, c.title, null, <span className="shrink-0 rounded-full bg-[#7C3AED]/10 px-2 py-0.5 text-xs font-bold text-[#7C3AED]">{c.date}</span>))}</ul>
          )}
        </SectionCard>

        <SectionCard testid="mkt-ov-activity-card" accent="#EC4899" icon={Rocket} eyebrow="Pipeline" title="Upcoming Influencer Activity" subtitle="Planned publications ahead">
          {activity.length === 0 ? <EmptyState icon={Rocket} title="Nothing planned" description="Planned influencer publications appear here." /> : (
            <ul className="space-y-3" data-testid="mkt-ov-activity-list">{activity.map((a) => row(a.id, a.name, null, <span className="shrink-0 text-xs font-semibold text-[#EC4899]">{a.date}</span>))}</ul>
          )}
        </SectionCard>
      </div>

      {/* 4 — Campaign performance area */}
      <SectionCard testid="mkt-ov-campaigns-card" accent="#0EA5A4" icon={Megaphone} eyebrow="Campaign performance" title="Active Campaigns" subtitle="Currently running across channels">
        {campaigns.length === 0 ? <EmptyState icon={Megaphone} title="None active" description="Active campaigns appear here." /> : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="mkt-ov-campaigns-list">{campaigns.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#E4E9F3] bg-white px-4 py-3">
              <div className="min-w-0"><p className="truncate text-sm font-semibold text-[#14245F]">{c.name}</p><p className="text-xs text-[#5A6B8C]">{c.channel || "Campaign"}</p></div>
              <StatusBadge status="success" label="Active" />
            </li>
          ))}</ul>
        )}
      </SectionCard>
    </div>
  );
};
